

import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Autocomplete,
    Box,
    Typography,
    IconButton,
    MenuItem,
    Snackbar,
    Alert,
    Tooltip,
    // MDBox
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { useAuth } from '../../auth/Auth';
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CustomFieldsSection from 'components/CustomFields/CustomFieldsSection';
import CircularProgress from '@mui/material/CircularProgress';
import { currencies } from 'utils/CurrencyList';
import colors from 'assets/theme/base/colors';
import { useCurrency } from 'context/CurrencyContext';
import {
    VEHICLE_NUMBER_HELPER,
    VEHICLE_NUMBER_PATTERN,
    validateOrderTransportAndDates,
} from 'utils/orderValidation';


function EditOrderModal({
    open,
    onClose,
    order,
    orderItems,
    onSave,
    suggestions,
    productList,
    transportDetails
}) {
    const [editedFields, setEditedFields] = useState({});
    const [editedOrderItems, setEditedOrderItems] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [deals, setDeals] = useState([]);
    const [contacts, setContacts] = useState([]);
    const { user, axiosInstance } = useAuth();
    const [formErrors, setFormErrors] = useState({});
    const [removedOrderItems, setRemovedOrderItems] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const customFieldsRef = useRef(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info'
    });
    const [customFields, setCustomFields] = useState([]);
    const [availableCurrencies, setAvailableCurrencies] = useState(currencies || []);
    const [transid, setTransid] = useState(transportDetails.transid || '');
    const [transname, setTransname] = useState(transportDetails.transname || '');
    const [transmode, setTransmode] = useState(transportDetails.transmode || '');
    const [vehno, setVehno] = useState(transportDetails.vehno || '');
    const [vehtype, setVehtype] = useState(transportDetails.vehtype || '');
    const [transdocno, setTransdocno] = useState(transportDetails.transdocno || '');
    const [transdocdate, setTransdocdate] = useState(transportDetails.transdocdate || '');
    const { formatCurrency, currency: systemCurrency } = useCurrency();

    // State for unit price editing
    const [editingPriceIndex, setEditingPriceIndex] = useState(null);
    const [editingPriceValue, setEditingPriceValue] = useState('');

    // Add this status options array at the top of your component
    const statusOptions = [
        { value: 'Draft', label: 'Draft' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Cancelled', label: 'Cancelled' }
    ];

    const activeCurrency = order.currency || systemCurrency;

    const combineAddress = (accountData) => {
        const shippingParts = [
            accountData?.shipping_address || '',
            accountData?.shipping_city || '',
            accountData?.shipping_postal_code || '',
            accountData?.shipping_state || '',
            accountData?.shipping_country || '',

        ].filter(part => part).join(', ');

        const billingParts = [
            accountData?.billing_address || '',
            accountData?.billing_city || '',
            accountData?.billing_postal_code || '',
            accountData?.billing_state || '',
            accountData?.billing_country || '',

        ].filter(part => part).join(', ');

        return { shippingAddress: shippingParts || '', billingAddress: billingParts || '' };
    };

    // Initialize state when modal opens
    useEffect(() => {
        if (open && order) {
            const { shippingAddress, billingAddress } = combineAddress(order);
            setEditedFields({
                ...order,
                order_date: order.order_date
                    ? new Date(order.order_date).toISOString().split('T')[0]
                    : null,
                account: order.account_id ? {
                    value: order.account_id,
                    label: order.account_name || ' ',
                    fullData: {
                        id: order.account_id,
                        name: order.account_name
                    }
                } : null,
                deal: order.deal_id ? {
                    value: order.deal_id,
                    label: order.deal_name || ' ',
                    fullData: {
                        id: order.deal_id,
                        name: order.deal_name
                    }
                } : null,
                contact: order.contact_id ? {
                    value: order.contact_id,
                    label: order.contact_name || '',
                    fullData: {
                        id: order.contact_id,
                        name: order.contact_name
                    }
                } : null,
                shipping_instruction: order.shipping_instruction || '',
                shipping_address: shippingAddress, // Use combined address
                billing_address: billingAddress,   // Use combined address
                shipping_method: order.shipping_method || '',
                delivery_date: order.delivery_date
                    ? new Date(order.delivery_date).toISOString().split('T')[0]
                    : null,
                transid: order.transid || '',
                transname: order.transname || '',
                transmode: order.transmode || '',
                vehno: order.vehno || '',
                vehtype: order.vehtype || '',
                transdocno: order.transdocno || '',
                // transdocdate: order.transdocdate || '',
            });

            setEditedOrderItems(orderItems.map(item => ({
                ...item,
                product_id: item.product_id,
                product_name: item.product_name,
                quantity: item.quantity,
                unit_price: item.unit_price,
                discount_percentage: item.discount_percentage,
                discounted_price: item.discounted_price,
                tax: item.tax || 0
            })));

            // Fetch contacts for the current account
            if (order.account_id) {
                fetchContacts(order.account_id);
            }
        }

        return () => {
            setEditedFields({});
            setEditedOrderItems([]);
        };
    }, [open, order, orderItems]);

    useEffect(() => {
        const fetchCustomFields = async () => {
            try {
                const response = await axiosInstance.get(`/custom-fields/orders`);
                setCustomFields(response.data || []);
            } catch (error) {
                console.error('Error fetching custom fields:', error);
                setCustomFields([]);
            }
        };

        fetchCustomFields();
    }, []);

    const fetchAccounts = async () => {
        try {
            const response = await axiosInstance.get('/accounts/');
            const formattedAccounts = response.data.map(account => ({
                value: account.id,
                label: account.name || `Account ${account.name}`,
                fullData: account
            }));
            setAccounts(formattedAccounts);
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    };

    const fetchContacts = async (accountId) => {
        try {
            if (!accountId) {
                setContacts([]);
                return;
            }

            const response = await axiosInstance.get(`/accounts/contacts/${accountId}`);
            const formattedContacts = response.data.map(contact => ({
                value: contact.id,
                label: contact.first_name
                    ? `${contact.first_name} ${contact.last_name || ''}`.trim()
                    : contact.last_name,
                fullData: contact
            }));

            setContacts(formattedContacts);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        }
    };

    const fetchDeals = async (accountId) => {
        try {
            if (!accountId) {
                setDeals([]);
                return;
            }

            const response = await axiosInstance.get(`/accounts/deals/${accountId}`);
            const formattedDeals = response.data.map(deal => ({
                value: deal.id,
                label: deal.name || `Deal ${deal.name}`,
                fullData: deal
            }));

            setDeals(formattedDeals);
        } catch (error) {
            console.error('Error fetching deals:', error);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    useEffect(() => {
        if (selectedAccount?.id) {
            fetchContacts(selectedAccount.id);
            fetchDeals(selectedAccount.id);
        } else {
            setContacts([]);
            setDeals([]);
        }
    }, [selectedAccount]);

    useEffect(() => {
        // Update state when transportDetails prop changes
        setTransid(transportDetails.transid || '');
        setTransname(transportDetails.transname || '');
        setTransmode(transportDetails.transmode || '');
        setVehno(transportDetails.vehno || '');
        setVehtype(transportDetails.vehtype || '');
        setTransdocno(transportDetails.transdocno || '');
        // setTransdocdate(transportDetails.transdocdate || '');
    }, [transportDetails]);

    const fetchAccountCurrency = async (accountId) => {
        if (!accountId) return;
        try {
            const response = await axiosInstance.get(`/accounts/${accountId}`);
            const accountData = response.data;
            const { shippingAddress, billingAddress } = combineAddress(accountData);
            setEditedFields(prev => ({
                ...prev,
                currency: accountData.currency || prev.currency,
                shipping_address: shippingAddress,
                billing_address: billingAddress
            }));
        } catch (error) {
            console.error('Error fetching account currency:', error);
        }
    };

    const handleFieldChange = (field, value) => {
        setEditedFields(prev => {
            const updates = {
                ...prev,
                [field]: value,
            };

            if (field === 'account') {
                updates.deal = null;
                updates.contact = null;
                updates.account_name = value?.label || '';

                if (value?.value) {
                    fetchContacts(value.value);
                    fetchDeals(value.value);
                    fetchAccountCurrency(value.value).then(() => {
                        axiosInstance.get(`/accounts/${value.value}`).then(response => {
                            const accountData = response.data;
                            const { shippingAddress, billingAddress } = combineAddress(accountData);
                            setEditedFields(prevFields => ({
                                ...prevFields,
                                shipping_address: shippingAddress,
                                billing_address: billingAddress
                            }));
                        }).catch(error => {
                            console.error('Error fetching account details:', error);
                        });
                    });
                } else {
                    setContacts([]);
                    setDeals([]);
                    updates.shipping_address = '';
                    updates.billing_address = '';
                }
            }

            if (field === 'deal') {
                updates.deal_name = value?.label || '';
            }
            if (field === 'contact') {
                updates.contact_name = value?.label || '';
            }

            return updates;
        });
    };


    // Handle order item changes
    // Update handleOrderItemChange method to calculate tax
    const handleOrderItemChange = (index, field, value) => {
        if (field === "discount_percentage") {
            if (value > 100) {
                value = 100;
            } else if (value < 0) {
                value = 0;
            }
        }
        const updatedItems = [...editedOrderItems];
        const item = updatedItems[index];

        if (field === 'product_name') {
            const selectedProduct = productList.find(p => p.name === value);
            if (selectedProduct) {
                item.product_name = value;
                item.unit_price = selectedProduct.price || 0;
                item.tax = selectedProduct.tax_rate || 0;

                // Check if this is a non-physical product (Service or Digital)
                if (selectedProduct.product_type === 'Service' || selectedProduct.product_type === 'Digital') {
                    // For non-physical products, show message if user tries to set quantity
                    // if (item.quantity !== 1) {
                    //     setSnackbar({
                    //         open: true,
                    //         message: `Setting quantity to 1 for non-physical product: ${selectedProduct.name}`,
                    //         severity: 'info'
                    //     });
                    // }
                    // Set product_type in the item
                    item.product_type = selectedProduct.product_type;
                    // Set quantity to 1 for non-physical products
                    // item.quantity = 1;
                } else {
                    // For physical products, retain the current quantity or default to 1
                    item.product_type = selectedProduct.product_type || 'Physical';
                }
            } else {
                // Reset row if no product is selected
                item.product_name = "";
                item.unit_price = 0;
                item.tax = 0;
                item.quantity = 0;
                item.discount_percentage = 0;
                item.discounted_price = 0;
                item.total_price = 0;
                item.product_type = null;
            }
        } else if (field === 'quantity') {
            // Check if it's a non-physical product before allowing quantity change
            const product = productList.find(p => p.name === item.product_name);

            if (product && (product.product_type === 'Service' || product.product_type === 'Digital')) {
                // Show message that quantities aren't tracked for non-physical products
                setSnackbar({
                    open: true,
                    message: `Fixed quantity of 1 for ${product.product_type} product: ${product.name}`,
                    severity: 'info'
                });
                // Set quantity to exactly 1 for non-physical products
                item.quantity = 1;
            } else {
                // For physical products or unknown, allow quantity change
                item[field] = value === '' ? 0 : parseFloat(value);
            }
        } else {
            // Handle other fields
            item[field] = field === 'product_name'
                ? value
                : (value === '' ? 0 : parseFloat(value));
        }

        // Calculate discounted price
        const unitPrice = parseFloat(item.unit_price) || 0;
        const discountPercentage = parseFloat(item.discount_percentage) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const taxRate = parseFloat(item.tax) || 0;

        // Calculate new values
        const discountAmount = unitPrice * (discountPercentage / 100);
        const discountedPrice = unitPrice - discountAmount;
        const subtotal = discountedPrice * quantity;
        // Tax on discounted net (after discount), not original unit price.
        const taxAmount = subtotal * (taxRate / 100);

        // Update calculated fields
        item.discounted_price = discountedPrice.toFixed(2);
        item.total_price = (subtotal + taxAmount).toFixed(2);
        item.tax_amount = taxAmount.toFixed(2);

        // Update state
        setEditedOrderItems(updatedItems);
    };


    // Remove order item
    const handleRemoveOrderItem = (index) => {
        // Check if there is more than one item before allowing removal
        if (editedOrderItems.length <= 1) {
            console.warn('Cannot remove the last order item. At least one item must remain.');
            return; // Prevent removal if this is the last item
        }

        const removedItem = editedOrderItems[index];
        console.log('Removing Item:', removedItem); // Log the item being removed
        setEditedOrderItems(prev => {
            const updatedItems = prev.filter((_, i) => i !== index);
            console.log('Updated Order Items:', updatedItems);
            return updatedItems;
        });

        if (removedItem?.id) {
            setRemovedOrderItems(prev => {
                const updatedList = [...prev, removedItem.id]; // Use the order item ID for removal
                console.log('Updated Removed Order Items:', updatedList);
                return updatedList;
            });
        } else {
            console.warn('Removed item has no ID:', removedItem); // Warn if no ID
        }
    };


    // Add custom fields handling
    const handleFieldUpdate = (field, value) => {
        console.log(`Updating field ${field} with value:`, value);
        setEditedFields(prev => {
            const newFields = { ...prev, [field]: value };
            console.log('Updated editedFields:', newFields);
            return newFields;
        });
    };

    const handleFieldError = (field, error) => {
        console.log(`Field error update: ${field}, Error: ${error}`); // Debug log
        if (error) {
            setFormErrors(prev => ({
                ...prev,
                [field]: error
            }));
        } else {
            setFormErrors(prev => {
                console.log(`Clearing error for ${field}, Previous errors:`, prev); // Debug log
                const { [field]: _, ...rest } = prev;
                return rest;
            });
        }
        console.log('Updated formErrors:', formErrors); // Debug log (note: this might log old state due to setState async nature)
    };

    // Update handleSave to include custom fields
    const handleSave = async () => {
        setLoading(true); // Start loading

        // Validate custom fields and update errors
        const isCustomFieldsValid = customFieldsRef.current?.validateAllFields() ?? true;

        const orderDateForValidation = editedFields.order_date || order.order_date;
        const fieldValidation = validateOrderTransportAndDates({
            deliveryDate: editedFields.delivery_date,
            orderDate: orderDateForValidation,
            vehno: editedFields.vehno,
            transmode: editedFields.transmode,
            existingVehno: order?.vehno,
        });
        if (!fieldValidation.valid) {
            setFormErrors((prev) => ({ ...prev, ...fieldValidation.errors }));
            setSnackbar({
                open: true,
                message: Object.values(fieldValidation.errors)[0] || 'Please fix the highlighted fields before saving.',
                severity: 'error',
            });
            setLoading(false);
            return;
        }

        // Always show snackbar if validation fails (ignore date/vehicle keys we just re-validated)
        const remainingErrors = Object.keys(formErrors).filter(
            (key) => key !== 'delivery_date' && key !== 'vehno'
        );
        if (!isCustomFieldsValid || remainingErrors.length > 0) {
            setSnackbar({
                open: true,
                message: "Please fill the required fields before saving.",
                severity: 'error'
            });
            setLoading(false); // Reset loading state here
            return; // Just return early instead of throwing an error
        }

        console.log('Removed Order Items before saving:', removedOrderItems);
        const totalAmount = calculateTotalAmount();
        const payload = {
            order: {
                ...editedFields,
                total_amount: totalAmount,
                subtotal: editedOrderItems.reduce((acc, item) => {
                    const unitPrice = parseFloat(item.unit_price) || 0;
                    const quantity = parseFloat(item.quantity) || 0;
                    const discountPercentage = parseFloat(item.discount_percentage) || 0;
                    const discountAmount = unitPrice * (discountPercentage / 100);
                    const discountedPrice = unitPrice - discountAmount;
                    return acc + (discountedPrice * quantity);
                }, 0),
                status: editedFields.status || order.status,
                order_date: editedFields.order_date || order.order_date,
                account_id: editedFields.account?.value,
                account_name: editedFields.account_name,
                deal_id: editedFields.deal?.value || '',
                deal_name: editedFields.deal_name || '',
                contact_id: editedFields.contact?.value,
                contact_name: editedFields.contact_name,
                shipping_address: editedFields.shipping_address,
                billing_address: editedFields.billing_address,
                shipping_method: editedFields.shipping_method,
                delivery_date: editedFields.delivery_date,
                po_number: editedFields.po_number,
                currency: editedFields.currency,
                vehno: fieldValidation.normalizedVehno,
                vehtype: editedFields.vehtype,
                transmode: editedFields.transmode,
                transname: editedFields.transname,
                transid: editedFields.transid,
                transdocno: editedFields.transdocno,
                transdocdate: editedFields.transdocdate,
                ...Object.keys(editedFields)
                    .filter(key => key.startsWith('cf_'))
                    .reduce((obj, key) => ({
                        ...obj,
                        [key]: editedFields[key],
                    }), {}),
            },
            orderItems: editedOrderItems
                .filter(item => item.product_name && item.quantity > 0)
                .map(item => ({
                    ...(item.id ? { id: item.id } : {}),
                    product_id: productList.find(p => p.name === item.product_name)?.id || null,
                    product_name: item.product_name,
                    quantity: item.quantity,
                    unit_price: parseFloat(item.unit_price || 0),
                    tax: item.tax || 0,
                    discount_percentage: item.discount_percentage || 0,
                    discounted_price: item.discounted_price || 0,
                    total_price: item.total_price || 0,
                    tax_amount: item.tax_amount // Include tax amount
                })),
            removedOrderItems, // Ensure removed items are included in the payload
        };

        console.log('Prepared Payload:', JSON.stringify(payload, null, 2));

        try {
            await onSave(payload); // Call the parent onSave function (handleSaveOrderChanges in OrderDetail)
            // Only close the modal on success
            onClose();
        } catch (error) {
            console.error("Error saving order:", error);
            // Do NOT close the modal here; keep it open so the user can correct errors
        } finally {
            setLoading(false); // Stop loading regardless of success or failure
        }
    };



    // Unit price editing functions
    const handleStartPriceEdit = (index, currentPrice) => {
        setEditingPriceIndex(index);
        setEditingPriceValue(currentPrice.toString());
    };

    const handleUnitPriceChange = (index, newPrice) => {
        const updatedItems = [...editedOrderItems];
        const item = updatedItems[index];
        
        // Update unit price
        item.unit_price = newPrice;
        
        // Calculate new values
        const unitPrice = parseFloat(newPrice) || 0;
        const discountPercentage = parseFloat(item.discount_percentage) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const taxRate = parseFloat(item.tax) || 0;

        const discountAmount = unitPrice * (discountPercentage / 100);
        const discountedPrice = unitPrice - discountAmount;
        const subtotal = discountedPrice * quantity;
        // Tax on discounted net (after discount), not original unit price.
        const taxAmount = subtotal * (taxRate / 100);

        // Update calculated fields
        item.discounted_price = discountedPrice.toFixed(2);
        item.total_price = (subtotal + taxAmount).toFixed(2);
        item.tax_amount = taxAmount.toFixed(2);
        
        setEditedOrderItems(updatedItems);
    };

    // Add new order item
    const handleAddOrderItem = () => {
        const newItem = {
            product_name: '',
            quantity: 1,
            tax: 0,
            unit_price: 0,
            discount_percentage: 0,
            total_price: 0
        };
        setEditedOrderItems(prev => [...prev, newItem]);
    };

    // Calculate total amount
    const calculateTotalAmount = () => {
        let subtotal = 0;
        let taxSummary = {}; // Object to store tax rates and their total amounts

        editedOrderItems.forEach((item) => {
            // Calculate subtotal (discounted price * quantity)
            const unitPrice = parseFloat(item.unit_price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            const discountPercentage = parseFloat(item.discount_percentage) || 0;
            const discountAmount = unitPrice * (discountPercentage / 100);
            const discountedPrice = unitPrice - discountAmount;
            const itemSubtotal = discountedPrice * quantity;
            subtotal += itemSubtotal;

            // Tax on discounted net (after discount).
            if (item.tax) {
                const taxRate = parseFloat(item.tax);
                const taxAmount = itemSubtotal * (taxRate / 100);
                taxSummary[taxRate] = (taxSummary[taxRate] || 0) + taxAmount;
            }

            // Update item tax_amount for consistency
            const taxRate = parseFloat(item.tax) || 0;
            item.tax_amount = (itemSubtotal * (taxRate / 100)).toFixed(2);
        });

        // Calculate total tax
        const totalTax = Object.values(taxSummary).reduce((acc, taxAmount) => acc + taxAmount, 0);

        // Grand total is subtotal + total tax
        const grandTotal = subtotal + totalTax;

        return grandTotal;
    };

    const renderCustomFields = () => {
        if (!customFields || customFields.length === 0) {
            return null;
        }
        return (
            <CustomFieldsSection
                ref={customFieldsRef}
                objectType="orders"
                objectData={editedFields}
                isEditing={true}
                onFieldUpdate={handleFieldUpdate}
                onError={handleFieldError}
                formErrors={formErrors}
            />
        );
    };

    useEffect(() => {
        if (Object.keys(formErrors).length > 0) {
            setSnackbar({
                open: true,
                message: "Please fill the required fields before saving.",
                severity: 'error'
            });
        }
    }, [formErrors]);

    // Add this useEffect after contacts are fetched
    useEffect(() => {
        if (editedFields.contact && contacts.length > 0) {
            const found = contacts.find(c => c.value === (editedFields.contact?.value || editedFields.contact));
            if (!found) {
                setEditedFields(prev => ({ ...prev, contact: contacts[0] }));
            }
        }
    }, [contacts]);

    // Add debug log before rendering the Contact Autocomplete
    console.log('Contact Autocomplete debug:', {
        editedContact: editedFields.contact,
        contactsArray: contacts
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{
            sx: {
                borderRadius: 2,
                overflow: 'hidden',
                width: '1200px',
                maxWidth: '100%'
            }
        }}>
            <Box className="pearl-popup-header"
                sx={{
                    backgroundColor: colors.popupHeader.background,
                    px: 3,
                    py: 2.5,
                }}
            >
                <MDTypography
                    variant="h6"
                    component="h2"
                    sx={{ color: '#FFFFFF' }}
                >
                    Edit Order
                </MDTypography>
            </Box>

            <DialogContent sx={{ p: 3, mt: 1 }}>
                <Grid container spacing={2}>
                    {/* Account Selection */}
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={accounts}
                            getOptionLabel={(option) => option?.label || ''}
                            value={editedFields.account || null}
                            onChange={(e, newValue) => handleFieldChange('account', newValue)}
                            isOptionEqualToValue={(option, value) =>
                                option?.value === value?.value
                            }
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    label="Account"
                                    fullWidth
                                    margin="normal"
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            height: "45px"
                                        }
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            }
                        />
                    </Grid>

                    {/* Deal Selection */}
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={deals}
                            getOptionLabel={(option) => option?.label || ''}
                            value={editedFields.deal || null}
                            onChange={(e, newValue) => handleFieldChange('deal', newValue)}
                            isOptionEqualToValue={(option, value) =>
                                option?.value === value?.value
                            }
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    label="Deal"
                                    fullWidth
                                    margin="normal"
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            height: "45px"
                                        }
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            }
                        />
                    </Grid>

                    {/* Contact Selection */}
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={contacts}
                            getOptionLabel={(option) => option?.label || ''}
                            value={contacts.find(c => c.value === (editedFields.contact?.value || editedFields.contact)) || null}
                            onChange={(e, newValue) => handleFieldChange('contact', newValue)}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    label="Contact"
                                    fullWidth
                                    margin="normal"
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            height: "45px"
                                        }
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            }
                        />
                    </Grid>

                    {/* Status Selection */}
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={statusOptions}
                            getOptionLabel={(option) => option.label}
                            value={statusOptions.find(s => s.value === editedFields.status) || null}
                            onChange={(e, newValue) => handleFieldChange('status', newValue?.value || 'Draft')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Status"
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            height: "45px"
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    {/* Other Fields */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="PO Number"
                            value={editedFields.po_number || ''}
                            onChange={(e) => handleFieldChange('po_number', e.target.value)}
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Shipping Address"
                            value={editedFields.shipping_address || ''}
                            onChange={(e) => handleFieldChange('shipping_address', e.target.value)}
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Billing Address"
                            value={editedFields.billing_address || ''}
                            onChange={(e) => handleFieldChange('billing_address', e.target.value)}
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    {/* <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={availableCurrencies}
                            getOptionLabel={(option) => `${option.name} ${option.code}`}
                            value={availableCurrencies.find(c => c.code === editedFields.currency) || null}
                            onChange={(e, newValue) => handleFieldChange('currency', newValue?.code || '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Currency"
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            height: "45px"
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid> */}

                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={availableCurrencies}
                            getOptionLabel={(option) => option.name || option.code || ''}
                            value={availableCurrencies.find(c => c.code === editedFields.currency) || null}
                            onChange={(e, newValue) => handleFieldChange('currency', newValue?.code || '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Currency"
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            height: "45px"
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Shipping Method"
                            value={editedFields.shipping_method || ''}
                            onChange={(e) => handleFieldChange('shipping_method', e.target.value)}
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Delivery Date"
                            type="date"
                            value={editedFields.delivery_date || ''}
                            onChange={(e) => {
                                handleFieldChange('delivery_date', e.target.value);
                                setFormErrors((prev) => {
                                    const next = { ...prev };
                                    delete next.delivery_date;
                                    return next;
                                });
                            }}
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                min: editedFields.order_date || undefined,
                            }}
                            error={!!formErrors.delivery_date}
                            helperText={formErrors.delivery_date || ''}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Notes"
                            value={editedFields.shipping_instruction || ''}
                            onChange={(e) => handleFieldChange('shipping_instruction', e.target.value)}
                            fullWidth
                            multiline
                            rows={4}
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    {/* Transport Details Section */}
                    <Grid container spacing={2} sx={{ m: .5 }}>
                        <Grid item xs={12}>
                            <MDTypography variant="h6" gutterBottom>
                                Transport Details
                            </MDTypography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Transporter ID"
                                value={editedFields.transid || ''}
                                onChange={(e) => handleFieldChange('transid', e.target.value)}
                                fullWidth
                                margin="normal"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Transporter Name"
                                value={editedFields.transname || ''}
                                onChange={(e) => handleFieldChange('transname', e.target.value)}
                                fullWidth
                                margin="normal"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Transport Mode"
                                value={editedFields.transmode || ''}
                                onChange={(e) => handleFieldChange('transmode', e.target.value)}
                                fullWidth
                                margin="normal"
                                select
                                InputProps={{
                                    sx: {
                                        height: '45px',
                                    },
                                }}
                                InputLabelProps={{ shrink: true }}
                            >
                                <MenuItem value="1">Road</MenuItem>
                                <MenuItem value="2">Rail</MenuItem>
                                <MenuItem value="3">Air</MenuItem>
                                <MenuItem value="4">Ship</MenuItem>
                            </TextField>
                        </Grid>

                        {/* Conditional Fields based on Transport Mode */}
                        {editedFields.transmode === '1' && ( // For Road
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Vehicle Number"
                                        value={editedFields.vehno || ''}
                                        onChange={(e) => {
                                            handleFieldChange('vehno', e.target.value.toUpperCase());
                                            setFormErrors((prev) => {
                                                const next = { ...prev };
                                                delete next.vehno;
                                                return next;
                                            });
                                        }}
                                        fullWidth
                                        margin="normal"
                                        helperText={formErrors.vehno || VEHICLE_NUMBER_HELPER}
                                        error={!!formErrors.vehno}
                                        inputProps={{
                                            pattern: VEHICLE_NUMBER_PATTERN,
                                            style: { textTransform: 'uppercase' },
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Vehicle Type"
                                        value={editedFields.vehtype || ''}
                                        onChange={(e) => handleFieldChange('vehtype', e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        select
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: '45px',
                                            },
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value="R">Regular</MenuItem>
                                        <MenuItem value="C">Container</MenuItem>
                                        <MenuItem value="O">Over Dimensional Cargo</MenuItem>
                                    </TextField>
                                </Grid>
                            </>
                        )}

                        {(['2', '3'].includes(editedFields.transmode)) && ( // For Rail or Air
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Transport Document Number"
                                    value={editedFields.transdocno || ''}
                                    onChange={(e) => handleFieldChange('transdocno', e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                    required
                                />
                            </Grid>
                        )}

                        {editedFields.transmode === '4' && ( // For Ship
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Transport Document Number"
                                        value={editedFields.transdocno || ''}
                                        onChange={(e) => handleFieldChange('transdocno', e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Vehicle Number"
                                        value={editedFields.vehno || ''}
                                        onChange={(e) => {
                                            handleFieldChange('vehno', e.target.value.toUpperCase());
                                            setFormErrors((prev) => {
                                                const next = { ...prev };
                                                delete next.vehno;
                                                return next;
                                            });
                                        }}
                                        fullWidth
                                        margin="normal"
                                        helperText={formErrors.vehno || VEHICLE_NUMBER_HELPER}
                                        error={!!formErrors.vehno}
                                        inputProps={{
                                            pattern: VEHICLE_NUMBER_PATTERN,
                                            style: { textTransform: 'uppercase' },
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>

                {/* Custom Fields Section */}
                {customFields && customFields.length > 0 && (
                    <Grid item xs={12} sx={{ mt: 3 }}>
                        <MDTypography variant="h6" gutterBottom>
                            Custom Fields
                        </MDTypography>
                        {renderCustomFields()}
                    </Grid>
                )}

                {/* Order Items Table */}
                <MDBox
                    sx={{
                        mt: 3,
                        mb: 3,
                        border: "1px solid #dee2e6",
                        borderRadius: "0.75rem",
                        overflow: "auto",
                        maxHeight: "400px"
                    }}
                >
                    {/* Header */}
                    <MDBox
                        sx={{
                            display: "flex",
                            backgroundColor: "#f8f9fa",
                            borderBottom: "1px solid #dee2e6",
                            position: "sticky",
                            top: 0,
                            zIndex: 1
                        }}
                    >
                        <MDBox width="25%" p={2}>
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Product Name
                            </MDTypography>
                        </MDBox>
                        <MDBox width="12%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Unit Price
                            </MDTypography>
                        </MDBox>
                        <MDBox width="8%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Tax %
                            </MDTypography>
                        </MDBox>
                        <MDBox width="12%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Discount %
                            </MDTypography>
                        </MDBox>
                        <MDBox width="12%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Discounted Price
                            </MDTypography>
                        </MDBox>
                        <MDBox width="10%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Quantity
                            </MDTypography>
                        </MDBox>
                        <MDBox width="12%" p={2} textAlign="right">
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Total Net Price
                            </MDTypography>
                        </MDBox>
                        <MDBox width="9%" p={2}>
                            <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                                Actions
                            </MDTypography>
                        </MDBox>
                    </MDBox>

                    {/* Body */}
                    <MDBox>
                        {editedOrderItems.map((item, index) => {
                            const discountedPrice = (item.unit_price || 0) - ((item.unit_price || 0) * (item.discount_percentage || 0) / 100);
                            const totalNetPrice = discountedPrice * (item.quantity || 0);
                            return (
                                <MDBox
                                    key={index}
                                    display="flex"
                                    sx={{
                                        borderBottom: "1px solid #dee2e6",
                                        '&:hover': { backgroundColor: '#f8f9fa' }
                                    }}
                                >
                                    <MDBox width="25%" p={2}>
                                        <Autocomplete
                                            options={suggestions.names}
                                            value={item.product_name}
                                            onChange={(e, newValue) =>
                                                handleOrderItemChange(index, 'product_name', newValue)
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': { borderColor: '#dee2e6' },
                                                            '&:hover fieldset': { borderColor: '#7b809a' }
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </MDBox>
                                    <MDBox width="12%" p={2} textAlign="right">
                                        {editingPriceIndex === index ? (
                                            <TextField
                                                value={editingPriceValue}
                                                onChange={(e) => {
                                                    const newPrice = parseFloat(e.target.value) || 0;
                                                    setEditingPriceValue(e.target.value);
                                                    handleUnitPriceChange(index, newPrice);
                                                }}
                                                onBlur={() => {
                                                    setEditingPriceIndex(null);
                                                    setEditingPriceValue('');
                                                }}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        setEditingPriceIndex(null);
                                                        setEditingPriceValue('');
                                                    }
                                                }}
                                                size="small"
                                                sx={{ 
                                                    width: '100%',
                                                    '& .MuiInputBase-root': {
                                                        height: '32px',
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                                inputProps={{ 
                                                    type: 'number', 
                                                    step: '0.01', 
                                                    min: '0',
                                                    style: { textAlign: 'center' }
                                                }}
                                                autoFocus
                                            />
                                        ) : (
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'space-between',
                                                width: '100%'
                                            }}>
                                                <MDTypography variant="button" fontWeight="regular">
                                                    {formatCurrency(item.unit_price || 0, activeCurrency)}
                                                </MDTypography>
                                                <Tooltip title="Edit Unit Price">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleStartPriceEdit(index, item.unit_price || 0)}
                                                        sx={{ 
                                                            color: 'text.secondary',
                                                            '&:hover': { color: 'primary.main' },
                                                            width: '24px',
                                                            height: '24px'
                                                        }}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        )}
                                    </MDBox>
                                    <MDBox width="8%" p={2} textAlign="right">
                                        <MDTypography variant="button" fontWeight="regular">
                                            {item.tax || 0}%
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox width="12%" p={2} textAlign="right">
                                        <TextField
                                            value={item.discount_percentage || 0}
                                            onChange={(e) =>
                                                handleOrderItemChange(
                                                    index,
                                                    'discount_percentage',
                                                    parseFloat(e.target.value) || 0
                                                )
                                            }
                                            type="number"
                                            size="small"
                                            sx={{
                                                width: '70px',
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: '#dee2e6' },
                                                    '&:hover fieldset': { borderColor: '#7b809a' }
                                                }
                                            }}
                                        />
                                    </MDBox>
                                    <MDBox width="12%" p={2} textAlign="right">
                                        <MDTypography variant="button" fontWeight="regular">
                                            {formatCurrency(discountedPrice, activeCurrency)}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox width="10%" p={2} textAlign="right">
                                        <TextField
                                            value={item.quantity || 0}
                                            onChange={(e) =>
                                                handleOrderItemChange(
                                                    index,
                                                    'quantity',
                                                    parseInt(e.target.value, 10) || 0
                                                )
                                            }
                                            type="number"
                                            size="small"
                                            sx={{
                                                width: '70px',
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: '#dee2e6' },
                                                    '&:hover fieldset': { borderColor: '#7b809a' }
                                                }
                                            }}
                                        />
                                    </MDBox>
                                    <MDBox width="12%" p={2} textAlign="right">
                                        <MDTypography variant="button" fontWeight="regular">
                                            {formatCurrency(totalNetPrice, activeCurrency)}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox width="9%" p={2} display="flex" alignItems="center">
                                        <IconButton
                                            color="error"
                                            onClick={() => handleRemoveOrderItem(index)}
                                            disabled={editedOrderItems.length <= 1}
                                            size="small"
                                        >
                                            <RemoveIcon />
                                        </IconButton>
                                        <IconButton
                                            color="info"
                                            onClick={handleAddOrderItem}
                                            size="small"
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    </MDBox>
                                </MDBox>
                            );
                        })}
                    </MDBox>
                </MDBox>

                {/* Totals Section */}
                <MDBox
                    sx={{
                        mt: 3,
                        p: 2,
                        borderTop: '2px solid #dee2e6',
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}
                >
                    <MDBox sx={{ minWidth: '300px' }}>
                        {(() => {
                            const summary = calculateTotalAmount();
                            const subtotal = editedOrderItems.reduce((acc, item) => {
                                const unitPrice = parseFloat(item.unit_price) || 0;
                                const quantity = parseFloat(item.quantity) || 0;
                                const discountPercentage = parseFloat(item.discount_percentage) || 0;
                                const discountAmount = unitPrice * (discountPercentage / 100);
                                const discountedPrice = unitPrice - discountAmount;
                                return acc + (discountedPrice * quantity);
                            }, 0);

                            const taxSummary = editedOrderItems.reduce((acc, item) => {
                                const unitPrice = parseFloat(item.unit_price) || 0;
                                const quantity = parseFloat(item.quantity) || 0;
                                const discountPercentage = parseFloat(item.discount_percentage) || 0;
                                const taxRate = parseFloat(item.tax) || 0;
                                const discountedPrice = unitPrice - (unitPrice * (discountPercentage / 100));
                                const taxAmount = (discountedPrice * quantity) * (taxRate / 100);

                                if (taxRate) {
                                    acc[taxRate] = (acc[taxRate] || 0) + taxAmount;
                                }
                                return acc;
                            }, {});

                            return (
                                <MDBox>
                                    <MDBox display="flex" justifyContent="space-between" mb={1}>
                                        <MDTypography variant="button" color="text" fontWeight="regular">
                                            Subtotal:
                                        </MDTypography>
                                        <MDTypography variant="button" color="text" fontWeight="medium">
                                            {formatCurrency(subtotal, activeCurrency)}
                                        </MDTypography>
                                    </MDBox>

                                    {Object.entries(taxSummary).map(([taxRate, taxAmount]) => (
                                        <MDBox key={taxRate} display="flex" justifyContent="space-between" mb={1}>
                                            <MDTypography variant="button" color="text" fontWeight="regular">
                                                Tax ({taxRate}%):
                                            </MDTypography>
                                            <MDTypography variant="button" color="text" fontWeight="medium">
                                                {formatCurrency(taxAmount, activeCurrency)}
                                            </MDTypography>
                                        </MDBox>
                                    ))}

                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        mt={2}
                                        pt={1.5}
                                        sx={{ borderTop: '1px solid #dee2e6' }}
                                    >
                                        <MDTypography variant="button" color="dark" fontWeight="medium">
                                            Total Amount:
                                        </MDTypography>
                                        <MDTypography variant="button" color="info" fontWeight="bold">
                                            {formatCurrency(summary, activeCurrency)}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            );
                        })()}
                    </MDBox>
                </MDBox>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <MDButton
                    onClick={onClose}
                    variant="outlined"
                    color="secondary"
                >
                    Cancel
                </MDButton>
                <MDButton
                    onClick={handleSave}
                    variant="gradient"
                    color="info"
                    disabled={Object.keys(formErrors).length > 0 || loading}
                >
                    {loading ? <CircularProgress size={24} /> : 'Save'}
                </MDButton>
            </DialogActions>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}

export default EditOrderModal;